import { Root, Thumb } from "@radix-ui/react-switch"
import clsx from "clsx"
import { forwardRef, useEffect, useId } from "react"
import { useFormContext } from "react-hook-form"

import { Skeleton } from "@particles/index"

import { FormControl, FormField, FormItem } from "@molecules/Form/Form"
import { InputName } from "@molecules/Form/inputs/Input/Input.types"

import { Label } from ".."
import styles from "./Toggle.module.scss"
import { ToggleProps } from "./Toggle.types"

function Toggle<T extends InputName = InputName>(props: ToggleProps<T>, ref: React.Ref<HTMLButtonElement>) {
    const {
        name = "",
        id,

        label = "",
        showLabel = false,
        supportText = "",
        showSupportText = false,

        alignment = "left",

        thumbProps,

        isDisabled = false,
        isLoading = false,
        isError = false,
        isPressed = false,
        _isFocused = false,
        _isHovered = false,

        isRequired = false,
        ...rest
    } = props

    const defaultId = useId()

    const renderContent = () => {
        return (
            <div className={styles.contentWrapper}>
                <div className={styles.labelWrapper}>
                    {isLoading ? (
                        <Skeleton
                            width={128}
                            height={10}
                            borderRadius={0}
                            containerClassName={styles[`${alignment}Align`]}
                        />
                    ) : (
                        <Label
                            label={label}
                            htmlFor={id || defaultId}
                            state={isError ? "error" : "default"}
                            isDisabled={isDisabled}
                        />
                    )}
                </div>
                {showSupportText && (
                    <div className={styles.supportTextWrapper}>
                        {isLoading ? (
                            <Skeleton width={254} height={10} borderRadius={0} />
                        ) : (
                            <span className={styles.supportText}>{supportText}</span>
                        )}
                    </div>
                )}
            </div>
        )
    }

    return (
        <Root
            className={clsx(styles.base, styles[`${alignment}Align`], {
                [styles.loading]: isLoading,
                [styles.error]: isError,
                [styles.focused]: _isFocused,
                [styles.hovered]: _isHovered,
                [styles.pressed]: isPressed,
            })}
            disabled={isDisabled}
            required={isRequired}
            id={id || defaultId}
            {...rest}
            name={String(name)}
            ref={ref}
        >
            {showLabel && alignment === "right" && renderContent()}
            <div className={styles.toggleWrapper}>
                {isLoading ? (
                    <Skeleton borderRadius={24} width={32} height={20} />
                ) : (
                    <div className={styles.toggle}>
                        <div>
                            <Thumb className={styles.thumb} {...thumbProps} />
                        </div>
                    </div>
                )}
            </div>
            {showLabel && alignment === "left" && renderContent()}
        </Root>
    )
}

const withController = <T extends InputName>(ToggleComponent: React.FC<Omit<ToggleProps<T>, "isControlled">>) => {
    const Controller = forwardRef<HTMLButtonElement, ToggleProps<T>>((props, ref) => {
        const { isControlled, ...rest } = props
        const form = useFormContext()

        if (isControlled) {
            return (
                <FormField
                    control={form.control}
                    name={props.name as string}
                    render={({ field }) => {
                        useEffect(() => {
                            if (props.value !== undefined) {
                                form.setValue(props.name as string, props.value)
                            }
                        }, [props.value])

                        return (
                            <FormItem>
                                <FormControl>
                                    <ToggleComponent
                                        {...rest}
                                        {...field}
                                        checked={field.value as boolean}
                                        onCheckedChange={field.onChange}
                                        onBlur={field.onBlur}
                                        ref={ref}
                                    />
                                </FormControl>
                            </FormItem>
                        )
                    }}
                />
            )
        } else {
            return <ToggleComponent {...rest} ref={ref} />
        }
    })

    Controller.displayName = "ToggleComponentController"

    return Controller as typeof Toggle
}

export default withController(forwardRef(Toggle))
